// src/components/ui/NotificationBell.jsx
import { useState, useRef, useEffect } from 'react';
import { FiBell, FiTrash2, FiClock, FiCheckSquare, FiAlertCircle, FiAlertTriangle, FiAtSign, FiCheckCircle, FiMessageCircle } from 'react-icons/fi';
import { useNotifications } from '../../contexts/NotificationContext';

// placement 'up' abre el panel hacia arriba (para el pie del sidebar).
const NotificationBell = ({ placement = 'down' }) => {
  const { notifications, unreadCount, markAsRead, markAllRead, clearAll } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getIcon = (type) => {
    switch (type) {
      case 'overdue': return <FiClock className="text-red-500 shrink-0" size={14} />;
      case 'upcoming': return <FiAlertTriangle className="text-amber-500 shrink-0" size={14} />;
      case 'assigned': return <FiCheckSquare className="text-sky-500 shrink-0" size={14} />;
      case 'completed': return <FiCheckCircle className="text-green-500 shrink-0" size={14} />;
      case 'mention': return <FiAtSign className="text-purple-500 shrink-0" size={14} />;
      case 'comment': return <FiMessageCircle className="text-blue-500 shrink-0" size={14} />;
      case 'error': return <FiAlertCircle className="text-orange-500 shrink-0" size={14} />;
      case 'reprogramacion_pendiente': return <FiAlertTriangle className="text-red-500 shrink-0" size={14} />;
      case 'calendar_reminder': return <FiClock className="text-indigo-500 shrink-0" size={14} />;
      default: return <FiBell className="text-gray-500 shrink-0" size={14} />;
    }
  };

  const timeAgo = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'ahora';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
        title="Notificaciones"
      >
        <FiBell size={18} className="text-gray-600 dark:text-slate-400" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
            style={{ minWidth: 18, height: 18, padding: '0 4px' }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className={`absolute ${placement === 'up' ? 'left-0 bottom-full mb-2' : 'right-0 top-full mt-1'} w-80 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-slate-700 z-50 overflow-hidden`}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-700">
            <h3 className="font-semibold text-sm text-gray-800 dark:text-slate-100">Notificaciones</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300 px-2 py-1 rounded hover:bg-sky-50 dark:hover:bg-sky-900/30 transition-colors"
                >
                  Marcar leidas
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={clearAll}
                  className="p-1 text-gray-400 dark:text-slate-500 hover:text-red-500 rounded hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                  title="Limpiar todo"
                >
                  <FiTrash2 size={14} />
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4">
                <FiBell size={24} className="text-gray-300 dark:text-slate-600 mb-2" />
                <p className="text-sm text-gray-400 dark:text-slate-500">Sin notificaciones</p>
              </div>
            ) : (
              notifications.slice(0, 20).map(notif => (
                <button
                  key={notif.id}
                  onClick={() => markAsRead(notif.id)}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors border-b border-gray-50 dark:border-slate-700/50 ${
                    !notif.read ? 'bg-sky-50/50 dark:bg-sky-900/15' : ''
                  }`}
                >
                  <div className="mt-0.5">{getIcon(notif.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs ${!notif.read ? 'font-semibold text-gray-800 dark:text-slate-100' : 'text-gray-600 dark:text-slate-300'}`}>
                      {notif.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 line-clamp-2">{notif.body}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[10px] text-gray-400 dark:text-slate-500">{timeAgo(notif.createdAt)}</span>
                    {!notif.read && (
                      <div className="w-2 h-2 bg-sky-500 rounded-full" />
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
